// Partenaires des organisations, écrans (conception du 29/10/2026, § 6 et 7) : droits, lignes de « Mes partenaires »
// et de « Partenariats », puces des hôtes des affaires transmises, pipeline d'une affaire.
import type { Identifier } from "ra-core";
import type {
  Deal,
  PartnerOrg,
  Partnership,
  ProjectShare,
  Sale,
} from "../types";
import type { Pipeline } from "./partnerOrg";

/** Accueille des partenaires : un membre de l'équipe, un administrateur, ou un membre abonné (même règle que la base,
 * `invite_partner_as`, et que la fonction `users`). */
export const canHostPartners = (
  sale:
    | Pick<Sale, "partner" | "administrator" | "partner_offer" | "disabled">
    | undefined
    | null,
) =>
  !!sale &&
  !sale.disabled &&
  (!sale.partner ||
    !!sale.administrator ||
    sale.partner_offer === "subscriber");

/** Retirer un partage : celui qui l'a fait, ou un administrateur de l'organisation (`unshare_project`). */
export const canUnshare = (
  share: ProjectShare,
  me: Identifier,
  orgAdmin: boolean,
) => orgAdmin || (share.shared_by != null && share.shared_by === me);

/** Partenaires actifs auxquels la fiche n'est pas encore partagée (`shares` : les partages de cette fiche). */
export const shareCandidates = (
  partnerships: Partnership[],
  shares: ProjectShare[],
): number[] => {
  const seeing = new Set(
    shares.filter((s) => !s.revoked_at).map((s) => s.partner_sale_id),
  );
  return partnerships
    .filter((p) => !p.ended_at && !seeing.has(p.partner_sale_id))
    .map((p) => p.partner_sale_id);
};

const fullName = (sale: Pick<Sale, "first_name" | "last_name">) =>
  `${sale.first_name ?? ""} ${sale.last_name ?? ""}`.trim();

export type PartnerRow = {
  partnershipId: number;
  saleId: number;
  name: string;
  orgName: string | null;
  subscriber: boolean;
  shares: number;
  deals: number;
  since: string;
};

/** « Mes partenaires » : liens actifs de mon organisation, fiches partagées et affaires transmises à chacun (une
 * affaire confiée à un collègue du partenaire compte pour son organisation). */
export const partnerRows = ({
  myOrgId,
  partnerships,
  sales,
  orgs,
  shares,
  deals,
}: {
  myOrgId: number;
  partnerships: Partnership[];
  sales: Sale[];
  orgs: PartnerOrg[];
  shares: ProjectShare[];
  deals: Deal[];
}): PartnerRow[] => {
  const saleById = new Map(sales.map((s) => [Number(s.id), s]));
  return partnerships
    .filter((p) => p.host_org_id === myOrgId && !p.ended_at)
    .map((p) => {
      const sale = saleById.get(p.partner_sale_id);
      const orgId = sale?.org_id ?? null;
      return {
        partnershipId: p.id,
        saleId: p.partner_sale_id,
        name: (sale && fullName(sale)) || `#${p.partner_sale_id}`,
        orgName: orgs.find((o) => o.id === orgId)?.name ?? null,
        subscriber: sale?.partner_offer === "subscriber",
        shares: shares.filter(
          (s) =>
            s.host_org_id === myOrgId &&
            s.partner_sale_id === p.partner_sale_id &&
            !s.revoked_at,
        ).length,
        deals: deals.filter((d) => {
          // Sans condition sur transmitted_by : les affaires confiées avant l'étape 2 ne l'ont pas.
          if (d.org_id !== myOrgId || d.archived_at) return false;
          if (Number(d.sales_id) === p.partner_sale_id) return true;
          const holder = saleById.get(Number(d.sales_id));
          return orgId != null && holder?.org_id === orgId;
        }).length,
        since: p.created_at,
      };
    });
};

/** Envoi d'une fiche à plusieurs partenaires : un partage chacun, l'affaire (une seule, un seul responsable) au
 * partenaire choisi parmi les cochés, transmise en dernier pour que les autres partages soient faits quoi qu'il
 * arrive. */
export const sharePlan = (
  selected: number[],
  dealTo: number | null,
): { partner: number; transmit: boolean }[] => [
  ...selected
    .filter((id) => id !== dealTo)
    .map((partner) => ({ partner, transmit: false })),
  ...selected
    .filter((id) => id === dealTo)
    .map((partner) => ({ partner, transmit: true })),
];

export type HistoryRow = {
  projectId: string;
  sharedAt: string;
  sharedBy: number | null;
  /** Partage retiré (par nous, ou à la fin d'un partenariat) ; null s'il voit toujours la fiche. */
  revokedAt: string | null;
  /** Affaire de la fiche, chez nous, tenue par lui ou un collègue de son organisation. */
  deal?: Deal;
};

/** Historique d'un partenaire dans « Mes partenaires » : les fiches que mon organisation lui a envoyées, la plus
 * récente d'abord, une ligne par fiche (le dernier partage compte), avec l'affaire transmise s'il y en a une. Une
 * affaire confiée avant l'étape 2, sans partage, fait entrer sa fiche à la date de l'affaire. */
export const partnerHistory = ({
  myOrgId,
  partnerSaleId,
  sales,
  shares,
  deals,
}: {
  myOrgId: number;
  partnerSaleId: number;
  sales: Sale[];
  shares: ProjectShare[];
  deals: Deal[];
}): HistoryRow[] => {
  const orgOf = (id: number) =>
    sales.find((s) => Number(s.id) === id)?.org_id ?? null;
  const partnerOrg = orgOf(partnerSaleId);
  const held = deals.filter((d) => {
    if (d.org_id !== myOrgId || !d.project_id) return false;
    const holder = Number(d.sales_id);
    return (
      holder === partnerSaleId ||
      (partnerOrg != null && orgOf(holder) === partnerOrg)
    );
  });
  const rows = new Map<string, HistoryRow>();
  for (const s of shares) {
    if (s.host_org_id !== myOrgId || s.partner_sale_id !== partnerSaleId)
      continue;
    const known = rows.get(s.project_id);
    if (known && known.sharedAt >= s.created_at) continue;
    rows.set(s.project_id, {
      projectId: s.project_id,
      sharedAt: s.created_at,
      sharedBy: s.shared_by,
      revokedAt: s.revoked_at,
    });
  }
  for (const d of held) {
    const projectId = d.project_id as string;
    const row = rows.get(projectId) ?? {
      projectId,
      sharedAt: d.created_at,
      sharedBy: d.transmitted_by == null ? null : Number(d.transmitted_by),
      revokedAt: null,
    };
    row.deal = d;
    rows.set(projectId, row);
  }
  return [...rows.values()].sort((a, b) =>
    b.sharedAt.localeCompare(a.sharedAt),
  );
};

export type HostRow = {
  hostOrgId: number;
  orgName: string | null;
  /** Liens actifs de cet hôte vers les membres de mon organisation. */
  links: { partnershipId: number; partnerSaleId: number }[];
  shares: number;
  deals: number;
  since: string;
};

/** « Partenariats » : une ligne par organisation hôte (plusieurs membres de mon organisation peuvent lui être liés),
 * fiches reçues par ces membres et affaires reçues de cet hôte, comptées une fois. */
export const hostRows = ({
  myOrgId,
  partnerships,
  orgs,
  shares,
  deals,
}: {
  myOrgId: number;
  partnerships: Partnership[];
  orgs: PartnerOrg[];
  shares: ProjectShare[];
  deals: Deal[];
}): HostRow[] => {
  const rows = new Map<number, HostRow>();
  for (const p of partnerships) {
    if (p.host_org_id === myOrgId || p.ended_at) continue;
    const row = rows.get(p.host_org_id) ?? {
      hostOrgId: p.host_org_id,
      orgName: orgs.find((o) => o.id === p.host_org_id)?.name ?? null,
      links: [],
      shares: 0,
      deals: deals.filter((d) => d.org_id === p.host_org_id && !d.archived_at)
        .length,
      since: p.created_at,
    };
    row.links.push({ partnershipId: p.id, partnerSaleId: p.partner_sale_id });
    row.shares += shares.filter(
      (s) =>
        s.host_org_id === p.host_org_id &&
        s.partner_sale_id === p.partner_sale_id &&
        !s.revoked_at,
    ).length;
    if (p.created_at < row.since) row.since = p.created_at;
    rows.set(p.host_org_id, row);
  }
  return [...rows.values()];
};

/** Liens à fermer pour quitter un hôte : le sien pour un membre, tous ceux de l'organisation pour son administrateur
 * (même règle que `leave_partnership`). */
export const linksToLeave = (
  row: HostRow,
  me: Identifier,
  orgAdmin: boolean,
): number[] =>
  row.links
    .filter((l) => orgAdmin || l.partnerSaleId === Number(me))
    .map((l) => l.partnershipId);

/** Affaire d'une fiche à montrer : celle de mon organisation, sinon celle que je suis (transmise par un hôte), sinon
 * la première visible. */
export const pickProjectDeal = (
  deals: Deal[],
  me: Identifier | undefined,
  myOrgId: number | null | undefined,
): Deal | undefined =>
  deals.find((d) => myOrgId != null && d.org_id === myOrgId) ??
  deals.find((d) => me != null && Number(d.sales_id) === Number(me)) ??
  deals[0];

export type HostChip = {
  orgId: number;
  name: string;
  count: number;
  overdue: boolean;
};

/** Puces de l'onglet « Transmises » : une par organisation hôte, la plus fournie d'abord, à égalité par nom. */
export const hostChips = (
  deals: Deal[],
  orgs: PartnerOrg[],
  myOrgId: number,
  today: string,
): HostChip[] => {
  const chips = new Map<number, HostChip>();
  for (const d of deals) {
    if (d.archived_at || d.org_id == null || d.org_id === myOrgId) continue;
    const chip = chips.get(d.org_id) ?? {
      orgId: d.org_id,
      name: orgs.find((o) => o.id === d.org_id)?.name ?? `#${d.org_id}`,
      count: 0,
      overdue: false,
    };
    chip.count += 1;
    chip.overdue ||= !!d.next_action_date && d.next_action_date < today;
    chips.set(d.org_id, chip);
  }
  return [...chips.values()].sort(
    (a, b) => b.count - a.count || a.name.localeCompare(b.name),
  );
};

/** Pipeline d'une affaire : le mien pour mes affaires ; celui de l'hôte pour une affaire transmise par une organisation
 * abonnée ; celui de la plateforme pour l'équipe Aubeon (ses étapes vivent dans la configuration) ou un hôte illisible. */
export const pipelineForDeal = (
  deal: Pick<Deal, "org_id"> | undefined | null,
  myOrg: PartnerOrg | undefined | null,
  orgs: PartnerOrg[],
  client: Pipeline,
): Pipeline => {
  const owner =
    deal?.org_id === myOrg?.id
      ? myOrg
      : orgs.find((o) => o.id === deal?.org_id);
  if (!owner || owner.client_team) return client;
  return {
    dealStages: owner.deal_stages,
    dealClosedStages: owner.deal_closed_stages,
  };
};

/** Filtre des onglets des affaires : mon organisation, ou les affaires transmises (d'un hôte choisi, ou de tous). */
export const dealTabFilter = (
  tab: "mine" | "transmitted",
  myOrgId: number,
  hostOrgId?: number | null,
) =>
  tab === "mine"
    ? { org_id: myOrgId }
    : hostOrgId != null
      ? { org_id: hostOrgId }
      : { "org_id@neq": myOrgId };

/** Hôte affiché dans l'onglet « Transmises » : le choix gardé s'il reste des affaires de cet hôte, sinon la première puce. */
export const chosenHost = (
  chips: HostChip[],
  stored: number | null,
): number | null =>
  chips.find((c) => c.orgId === stored)?.orgId ?? chips[0]?.orgId ?? null;

/** Case « Lui transmettre aussi l'affaire » : cochée sauf si quelqu'un d'autre suit déjà l'affaire (un collègue ou un
 * premier partenaire), à qui la transmission la reprendrait. */
export const transmitByDefault = (
  deal: Pick<Deal, "sales_id"> | undefined,
  me: Identifier,
) => !deal || deal.sales_id == null || Number(deal.sales_id) === Number(me);
